import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { socket } from "../../socket";
import ConnectionManager from "./ConnectionManager";

import classes from "./Messages.module.css";
import { API_URL } from "../../config";

function Messages({ user }) {
  const [isSocketConnected, setIsSocketConnected] = useState(
    socket.connected
  );

  const { chatId } = useParams();
  const messageInputRef = useRef();

  const [receiverUser, setReceiverUser] = useState();
  const [messages, setMessages] = useState([]);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState(null);

  const addMessageHandler = (msg) => {
    setMessages((prev) => [msg, ...prev]);
  };

  const sendMessageHandler = (msg) => {
    // console.log(msg);
    socket.emit("sendMessage", msg);
  };

  useEffect(() => {
    const onConnect = () => {
      setIsSocketConnected(true);
      socket.emit("authorize", {
        token: user.token,
        username: user.username,
      });
    };
    const onDisconnect = () => {
      console.log("Socket disconnected");
      setIsSocketConnected(false);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("receiveMessage", addMessageHandler);

    socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("receiveMessage", addMessageHandler);

      socket.disconnect();
    };
  }, [user]);

  useEffect(() => {
    setIsWorking(true);
    setError(null);
    // Getting user which we are trying to talk
    fetch(API_URL + "api/messages/user/" + chatId, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + user.token,
      },
    })
      .then((response) => {
        if (!response.ok)
          setError({
            message: `(${response.status}) ${response.statusText} ${
              response.ok ? "OK" : "NOT OK"
            }`,
          });
        else return response.json();
      })
      .then((data) => {
        setReceiverUser(data);
        // Getting messages
        fetch(API_URL + "api/messages/" + chatId, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + user.token,
          },
        })
          .then((response) => {
            if (!response.ok)
              setError({
                message: `(${response.status}) ${
                  response.statusText
                } ${response.ok ? "OK" : "NOT OK"}`,
              });
            else return response.json();
          })
          .then((data) => {
            setMessages(data);
          })
          .catch((err) => {
            setError({
              message: "Can't fetch messages. See console",
            });
            console.log(err);
          });
      })
      .catch((err) => {
        setError({ message: "Can't fetch messages. See console" });
        console.log(err);
      })
      .finally(() => setIsWorking(false));
  }, [user.token, chatId]);

  const sendMessage = useCallback(
    (e) => {
      e.preventDefault();
      if (messageInputRef.current.value.length < 1) return;
      setIsWorking(true);
      setError(null);
      fetch(API_URL + "api/messages/" + chatId, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + user.token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          value: messageInputRef.current.value,
        }),
      })
        .then((response) => {
          if (!response.ok)
            setError({
              message: `(${response.status}) ${response.statusText} ${
                response.ok ? "OK" : "NOT OK"
              }`,
            });
          else return response.json();
        })
        .then((data) => {
          messageInputRef.current.value = "";
          addMessageHandler(data);
          if (receiverUser && receiverUser.id !== user.id)
            sendMessageHandler(data);
        })
        .catch((err) => {
          setError({ message: "Can't send message. See console" });
          console.log(err);
        })
        .finally(() => setIsWorking(false));
    },
    [chatId, user.token, user.id, receiverUser]
  );

  return (
    <div className={classes.messages}>
      <div>
        Chat is {isSocketConnected ? "connected" : "disconnected"}
        <ConnectionManager isSocketConnected={isSocketConnected} />
      </div>

      {isWorking && <div style={{ display: "none" }}>Loading...</div>}
      {error && (
        <div style={{ display: "none" }}>Errror: {error.message}</div>
      )}
      <div className={classes.title}>
        {receiverUser && <span>{receiverUser.username}</span>}
      </div>

      {messages && messages.length ? (
        <ul className={classes.list}>
          {messages.toReversed().map((message) => (
            <>
              {/* <- This generates an error: Each child should have unique "key" prop */}
              {message.receiverId === user.id ? (
                <li
                  // k={`${message.id}_badge_1`}
                  key={`${message.id}_badge_1`}
                  className={`${classes.badge} ${classes.userBadge}`}
                >
                  {receiverUser.username}
                </li>
              ) : (
                <li
                  // k={`${message.id}_badge_2`}
                  key={`${message.id}_badge_2`}
                  className={`${classes.badge} ${classes.receiverBadge}`}
                >
                  {"You"}
                </li>
              )}
              <li
                // k={message.id}
                key={message.id}
                className={`${classes.message} ${
                  message.receiverId === user.id
                    ? classes.receivedMessage
                    : classes.sentMessage
                }`}
              >
                {message.value}
              </li>
            </>
          ))}
        </ul>
      ) : (
        <div>No messages found</div>
      )}
      <form className={classes.inputForm} onSubmit={sendMessage}>
        <input
          type='text'
          placeholder='Type your message here...'
          ref={messageInputRef}
        />
        <button type='submit' disabled={isWorking}>
          Send
        </button>
      </form>
    </div>
  );
}

export default Messages;
